import axios from "axios";
import { shareLinkEndpoint } from '../_constants/api.constant';
import { getSelfToken } from '../../_helpers/auth/secureToken';

// The two surfaces the backend mints gn2.me short links for. A song link needs
// the song id in its path, the music hub is always the logged-in creator.
export const SHARE_SURFACE = {
    MUSIC_HUB: 'MUSIC_HUB',
    SONG: 'SONG',
};

// '.../share-link/', '.../share-link/alias/', etc. for either surface.
function shareLinkUrl(surface, songId, suffix = '') {
    if (surface === SHARE_SURFACE.SONG) {
        return `${shareLinkEndpoint.SONG}/${songId}/share-link/${suffix}`;
    }
    return shareLinkEndpoint.MUSIC_HUB + suffix;
}

function authHeader() {
    return { headers: { Authorization: 'Token ' + getSelfToken() } };
}

// Every call resolves with the response, error responses included: the backend
// answers a refusal with a typed `reason` the dialog renders, so a 409 or 400
// is information rather than a failure.
function settle(promise) {
    return promise
        .then(function (response) { return response; })
        .catch(function (error) { return error.response; });
}

// GET the link. Mints one on the first call, so `branded: false` means the
// gn2.me registration is still in flight and the caller should poll.
export async function getShareLink(surface, songId) {
    return settle(axios.get(shareLinkUrl(surface, songId), authHeader()));
}

// The same GET, told not to mint: `?create=false`.
//
// This exists because the Song Hub has to LABEL its button — "Create short URL"
// or "View short URL" — before anyone presses it, and the song cards have to
// answer a hover with the link's address. Asking the ordinary way would answer
// both questions by creating the very thing it was asked to look for, and a
// list of thirty songs would mint thirty gn2.me links nobody asked for.
//
// A backend that does not know the parameter simply mints as before, so the
// worst case is today's behaviour rather than a broken screen. Callers must
// treat a 404 and a `url`-less body identically: both mean "no link yet".
export async function getShareLinkStatus(surface, songId) {
    return settle(axios.get(
        shareLinkUrl(surface, songId),
        { params: { create: false }, ...authHeader() }
    ));
}

// True when a probe response describes a link that actually exists.
export function hasShareLink(response) {
    const body = response && response.data;
    if (!body || typeof body !== 'object' || Array.isArray(body)) return false;
    return Boolean(body.url);
}

// DELETE retires the link at gn2.me. A later getShareLink() mints a fresh one
// rather than resurrecting this code.
export async function revokeShareLink(surface, songId) {
    return settle(axios.delete(shareLinkUrl(surface, songId), authHeader()));
}

// PATCH a custom alias. Once a link has been issued the backend refuses with
// 409 `alias_change_breaks_existing` until confirm is passed, because the
// change breaks every copy already shared.
export async function updateShareLinkAlias(surface, songId, alias, confirm = false) {
    return settle(axios.patch(
        shareLinkUrl(surface, songId, 'alias/'),
        { alias: alias, confirm_breaks_existing: confirm },
        authHeader()
    ));
}

// GET click analytics, read live from gn2.me. days = 0 means all time.
export async function getShareLinkAnalytics(surface, songId, days = 30) {
    return settle(axios.get(
        shareLinkUrl(surface, songId, 'analytics/'),
        { params: { days: days }, ...authHeader() }
    ));
}

// GET the QR code. gn2.me returns image bytes, so the backend hands back a
// base64 `qr_code_data_uri` that goes straight into an <img src>.
//
// `format` is deliberately NOT sent. DRF reserves that query parameter for
// renderer selection, so `?format=png` makes it look for a renderer named
// "png", find none, and answer 404 before the view ever runs. PNG is the
// endpoint's default, so leaving it off asks for the same image.
export async function getShareLinkQr(surface, songId, params = {}) {
    const { format, ...safeParams } = params;
    return settle(axios.get(
        shareLinkUrl(surface, songId, 'qr/'),
        { params: { size: 512, ecl: 'M', ...safeParams }, ...authHeader() }
    ));
}

// ---------------------------------------------------------------------------
// How to read a gn2.me analytics payload.
//
// The backend passes the provider's body through untouched, so these keys are
// gn2.me's own spelling — `deviceType`, `referer` with one r, `utm_source` —
// and their published API reference is the documentation for them too. The
// same lists drive the Link-in-Bio analytics screen in the creator platform;
// they are duplicated here rather than shared because the two apps ship
// separately, and a dimension that appears in one and not the other would be
// a silent difference in what a creator is told.
// ---------------------------------------------------------------------------

// Why gn2.me could not answer. Nothing here is the creator's fault, so each
// one is worded as a state rather than as a failure.
export const LINK_ANALYTICS_REASON_COPY = {
    disabled: 'Short links are switched off right now.',
    not_branded:
        'This link has no gn2.me address yet, so there is nothing to measure. '
        + 'Numbers appear once the link is registered.',
    unauthorized: 'We could not reach gn2.me. Please try again later.',
    insufficient_scope: 'We do not have permission to read these numbers.',
    link_not_found: 'gn2.me has no record of this link.',
    rate_limited: 'gn2.me is rate-limiting us. Numbers will be back shortly.',
    provider_unavailable: 'gn2.me is not responding right now. Try again in a moment.',
    provider_timeout: 'gn2.me took too long to answer. Try again in a moment.',
    invalid_response: 'gn2.me sent something we could not read.',
};

export function linkAnalyticsReasonCopy(reason) {
    return LINK_ANALYTICS_REASON_COPY[String(reason || '')]
        || 'Click analytics are unavailable right now.';
}

// Why the backend refused to mint a link at all. These two ARE the creator's
// to fix, so they get an instruction rather than a state.
export const NOT_SHAREABLE_MESSAGE = {
    handle_not_set: 'Add your artist handle in your profile first — your short URL is built from it.',
    not_shareable: 'Give this song a title and slug first — the short URL is built from them.',
};

// `days=0` is all time. The server clamps anything past a year.
export const LINK_ANALYTICS_DAY_OPTIONS = [
    { value: 7, label: '7 days' },
    { value: 30, label: '30 days' },
    { value: 90, label: '90 days' },
    { value: 0, label: 'All time' },
];

// The four numbers the creator came for. Clicks and unique visitors answer
// "did anyone press it"; conversions and the rate answer "did it work".
export const LINK_ANALYTICS_HERO_FIELDS = [
    { key: 'totalClicks', label: 'Clicks', format: 'count', hint: 'Counted at the redirect' },
    { key: 'uniqueVisitors', label: 'Unique visitors', format: 'count', hint: 'Repeat clicks counted once' },
    { key: 'conversions', label: 'Conversions', format: 'count', hint: 'Clicks that completed a goal' },
    { key: 'conversionRate', label: 'Conversion rate', format: 'percent', hint: 'Conversions per click' },
];

// Reach and value. Real data, but the follow-up question rather than the one
// anyone opened the page with — so a strip, not four more cards.
export const LINK_ANALYTICS_SECONDARY_FIELDS = [
    { key: 'uniqueCountriesCount', label: 'Countries', format: 'count' },
    { key: 'uniqueDevicesCount', label: 'Devices', format: 'count' },
    { key: 'uniqueBrowsersCount', label: 'Browsers', format: 'count' },
    { key: 'totalConversionValue', label: 'Conversion value', format: 'count' },
];

// The breakdowns, and how each is drawn. `form` is a property of the DATA, not
// a style preference: a share of a small closed set reads as a ring, an
// open-ended list with a tail people scan reads as ranked rows.
//
// `date` and `hourly` are absent on purpose — they are the two time charts,
// drawn across the top of the tab rather than listed as another card.
export const LINK_ANALYTICS_BREAKDOWNS = [
    {
        key: 'deviceType',
        label: 'Device breakdown',
        form: 'donut',
        subtitle: 'Clicks by device type.',
    },
    {
        key: 'browser',
        label: 'Browser distribution',
        form: 'donut',
        subtitle: 'Clicks by browser.',
    },
    {
        key: 'os',
        label: 'OS breakdown',
        form: 'donut',
        subtitle: 'Clicks by operating system.',
    },
    {
        key: 'country',
        label: 'Geography',
        form: 'bars',
        // The one dimension with a second honest reading. `code` on each row is
        // an ISO-2 country code, which is what the map keys its shapes by.
        map: true,
        subtitle: 'Countries your listeners click from.',
    },
    {
        key: 'city',
        label: 'Cities',
        form: 'donut',
        subtitle: 'Cities your listeners click from.',
        emptyNote: 'No cities resolved for these clicks yet.',
    },
    {
        key: 'utm_source',
        label: 'Traffic sources',
        form: 'donut',
        subtitle: 'Clicks by utm_source parameter.',
        emptyNote:
            'No clicks yet. Once the link is shared, untagged traffic appears here as "Direct".',
    },
    {
        key: 'referer',
        label: 'Referrer sources',
        form: 'donut',
        // Raw referrers arrive split across hosts — `l.instagram.com` and
        // `www.instagram.com` are one platform competing with itself for rows.
        group: true,
        subtitle: 'The page a click came from, grouped by platform.',
        // The most-asked question about this screen, answered in the card
        // rather than in a support thread.
        emptyNote:
            'A referrer only exists when the click came from a web page. Taps from the '
            + 'Instagram app, WhatsApp, DMs and QR scans send none — so this is usually '
            + 'empty, and those clicks show under Traffic sources as "Direct".',
    },
    {
        key: 'language',
        label: 'Language',
        form: 'donut',
        subtitle: 'Browser language of the people clicking.',
    },
];

// The buckets `groupLinkSources` folds raw referrer hosts into. Ordered: the
// first matching test wins, and the last entry has no test so nothing escapes.
export const LINK_SOURCE_GROUPS = [
    { key: 'instagram', label: 'Instagram', color: '#EC4899', test: (v) => v.includes('instagram') || v.includes('ig.me') },
    { key: 'facebook', label: 'Facebook', color: '#2563EB', test: (v) => v.includes('facebook') || v.includes('fb.') },
    { key: 'youtube', label: 'YouTube', color: '#DC2626', test: (v) => v.includes('youtube') || v.includes('youtu.be') },
    { key: 'spotify', label: 'Spotify', color: '#16A34A', test: (v) => v.includes('spotify') },
    { key: 'tiktok', label: 'TikTok', color: '#0F172A', test: (v) => v.includes('tiktok') },
    { key: 'whatsapp', label: 'WhatsApp', color: '#25D366', test: (v) => v.includes('whatsapp') || v.includes('wa.me') },
    { key: 'x', label: 'X / Twitter', color: '#334155', test: (v) => v.includes('twitter') || v === 't.co' || v.includes('//t.co') },
    { key: 'search', label: 'Search', color: '#0891B2', test: (v) => v.includes('google') || v.includes('bing') || v.includes('duckduckgo') },
    { key: 'direct', label: 'Direct', color: '#3B82F6', test: (v) => !v || v === 'direct' || v === '(direct)' || v === 'none' },
    { key: 'other', label: 'Other', color: '#6B7280', test: null },
];

// Groups gn2.me's `breakdowns.referer` rows into the buckets above.
// Returns `[{ key, label, color, clicks, uniqueVisitors }]`, biggest first,
// empty buckets dropped.
export function groupLinkSources(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const totals = new Map();
    rows.forEach((row) => {
        const name = String((row && row.name) || '').toLowerCase();
        const group = LINK_SOURCE_GROUPS.find((g) => (g.test ? g.test(name) : true));
        const bucket = totals.get(group.key)
            || { key: group.key, label: group.label, color: group.color, clicks: 0, uniqueVisitors: 0 };
        bucket.clicks += Number(row && row.clicks) || 0;
        bucket.uniqueVisitors += Number(row && row.uniqueVisitors) || 0;
        totals.set(group.key, bucket);
    });
    return [...totals.values()]
        .filter((b) => b.clicks > 0)
        .sort((a, b) => b.clicks - a.clicks);
}

// A summary number, rendered.
//
// A dash, never a zero, when the field is absent. An unavailable payload has no
// `summary` at all, and a 0 printed where we simply never asked is a confident
// claim about traffic we did not measure.
export function formatLinkMetric(value, format) {
    if (value === null || value === undefined || value === '') return '—';
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    if (format === 'percent') {
        // gn2.me's `conversionRate` is already a percentage (7.6923 means
        // 7.69%). Two decimals below 10, none above — 7.69% is a meaningful
        // difference from 7.7%, 43.19% is not from 43%.
        const fixed = n.toFixed(n >= 10 ? 0 : 2);
        // Trailing zeros go only when there is a decimal point to strip them
        // from. Blindly stripping `0+$` turns 20 into 2.
        return `${fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed}%`;
    }
    return n.toLocaleString();
}
